import { Component, computed, inject, Signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

import { AbstractDialog, AbstractDialogResultData, DialogComponent } from '@next/core-lib/dialog';
import { IrrigationsStore } from '@app/irrigations/store/irrigations.store';
import { CropFieldWithIrrigation, RegisterIrrigation, SelectedCropField } from '@app/irrigations/models/models';
import {
  IrrigationDialogOverviewComponent,
} from '@app/irrigations/components/irrigation-dialog-overview/irrigation-dialog-overview.component';
import {
  IrrigationDialogRegisterFormComponent,
} from '@app/irrigations/components/irrigation-dialog-register-form/irrigation-dialog-register-form.component';
import {
  IrrigationDialogCropFieldSelectionComponent,
} from '@app/irrigations/components/irrigation-dialog-crop-field-selection/irrigation-dialog-crop-field-selection.component';
import {
  CropFieldDeselectEvent,
  IrrigationDeleteEvent,
  IrrigationDialogData,
  IrrigationDialogResult,
  IrrigationRegisterEvent,
} from '@app/irrigations/models/ui-models';
import { MatDivider } from '@angular/material/list';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@next/core-lib/i18n';
import { FmsSyncStore } from '@app/shared/fms-sync/fms-sync.store';

@Component({
  selector: 'app-irrigation-dialog',
  templateUrl: './irrigation-dialog.component.html',
  styleUrls: ['./irrigation-dialog.component.scss'],
  imports: [
    DialogComponent,
    FormsModule,
    ReactiveFormsModule,
    MatProgressSpinner,
    IrrigationDialogOverviewComponent,
    IrrigationDialogRegisterFormComponent,
    IrrigationDialogCropFieldSelectionComponent,
    MatDivider,
    MatIcon,
    TranslatePipe,
  ],
  providers: [IrrigationsStore],
})
export class IrrigationDialogComponent extends AbstractDialog<IrrigationDialogResult> {
  readonly data = inject<IrrigationDialogData>(MAT_DIALOG_DATA);

  protected readonly mdDialogRef = inject<
    MatDialogRef<IrrigationDialogComponent, AbstractDialogResultData<IrrigationDialogResult>>
  >(MatDialogRef);
  private readonly irrigationsStore = inject(IrrigationsStore);
  private readonly fmsSyncStore = inject(FmsSyncStore);

  protected readonly isFmsReadonlyMode: Signal<boolean> = computed(() => this.fmsSyncStore.isEnabled());
  protected readonly isReadonlyMode: Signal<boolean> = computed(
    () => this.isFmsReadonlyMode() || Boolean(this.data.readOnly),
  );
  protected readonly isPastYearReadonlyMode: Signal<boolean> = computed(() => Boolean(this.data.readOnly));
  protected readonly isLoading: Signal<boolean> = this.irrigationsStore.irrigationsIsLoading;
  protected readonly fmsReadonlyMessageKey = 'shared.diseaseTable.fmsManagedActionsMessage';

  protected readonly cropFieldsWithIrrigations: Signal<CropFieldWithIrrigation[]> = this.irrigationsStore.sortedCropFieldsWithIrrigations;
  protected readonly selectedCropFields: Signal<SelectedCropField[]> = this.irrigationsStore.sortedSelectedCropFields;

  constructor() {
    super();
    this.irrigationsStore.selectCropFields(this.data.selectedCropFields);
  }

  override cancel(result?: IrrigationDialogResult): void {
    super.cancel(result ?? { modifiedCropFieldIds: this.irrigationsStore.modifiedCropFieldIds() });
  }

  registerIrrigation(registerIrrigation: IrrigationRegisterEvent): void {
    if (this.isReadonlyMode()) {
      return;
    }

    const { amountInMillimeters, startedOn, endedOn, type } = registerIrrigation;

    const cropFieldIds = this.selectedCropFields().map((cf) => cf.id);
    const cropYear = startedOn.getFullYear();

    const irrigation: RegisterIrrigation = {
      cropFieldIds,
      cropYear,
      startedOn,
      endedOn,
      amountInMillimeters,
      type,
    };

    this.irrigationsStore.registerIrrigation(irrigation);
  }

  deleteIrrigation(deleteIrrigation: IrrigationDeleteEvent): void {
    if (this.isReadonlyMode()) {
      return;
    }

    const { cropFieldId, irrigation } = deleteIrrigation;

    this.irrigationsStore.deleteIrrigation({
      cropFieldId,
      irrigationId: irrigation.id,
    });
  }

  deselectCropField(deselectCropField: CropFieldDeselectEvent): void {
    if (this.isReadonlyMode()) {
      return;
    }

    this.irrigationsStore.deselectCropField(deselectCropField.id);
  }
}
